import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_STYLES = {
  book_overdue: { bg: 'bg-red-50', border: 'border-red-200', icon: '📕' },
  fine_generated: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: '💰' },
  book_issued: { bg: 'bg-green-50', border: 'border-green-200', icon: '📗' },
  book_returned: { bg: 'bg-blue-50', border: 'border-blue-200', icon: '📘' },
  domain_verified: { bg: 'bg-green-50', border: 'border-green-200', icon: '🌐' },
  domain_failed: { bg: 'bg-red-50', border: 'border-red-200', icon: '🌐' },
  attendance_alert: { bg: 'bg-orange-50', border: 'border-orange-200', icon: '⚠️' },
  payroll_generated: { bg: 'bg-purple-50', border: 'border-purple-200', icon: '💼' },
  transport_alert: { bg: 'bg-blue-50', border: 'border-blue-200', icon: '🚌' },
  exam_result: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: '📝' },
  announcement: { bg: 'bg-orange-50', border: 'border-orange-200', icon: '📢' },
  system: { bg: 'bg-gray-50', border: 'border-gray-200', icon: '⚙️' },
};

export default function NotificationBell({ token }) {
  const { language } = useLanguage();
  const t = (hi, en) => language === 'hi' ? hi : en;
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/notifications/unread-count`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
      if (d.success) setUnread(d.unread_count);
    }).catch(() => {});
  }, [token]);

  const fetchNotifs = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/notifications/?limit=20`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
      if (d.success) {
        setNotifs(d.notifications);
        setUnread(d.unread_count);
      }
    }).catch(() => {});
  }, [token]);

  // Poll unread count every 30s
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const toggleOpen = () => {
    if (!open) fetchNotifs();
    setOpen(!open);
  };

  const markRead = (id) => {
    fetch(`${API_URL}/api/notifications/${id}/read`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` }
    }).then(() => {
      setNotifs(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    });
  };

  const markAllRead = () => {
    fetch(`${API_URL}/api/notifications/read-all`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` }
    }).then(() => {
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    });
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('अभी', 'Just now');
    if (mins < 60) return `${mins}${t('मि.', 'm')}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}${t('घं.', 'h')}`;
    const days = Math.floor(hrs / 24);
    return `${days}${t('दि.', 'd')}`;
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      {/* Bell Button */}
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-lg hover:bg-white/20 transition"
        data-testid="notification-bell-btn"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border z-50 max-h-[70vh] flex flex-col" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-xl">
            <h3 className="font-bold text-gray-800 text-sm">{t('सूचनाएं', 'Notifications')} {unread > 0 && <span className="text-xs text-orange-500">({unread})</span>}</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-orange-500 hover:text-orange-700 font-medium" data-testid="mark-all-read-btn">
                {t('सब पढ़ा', 'Mark all read')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1" data-testid="notification-list">
            {notifs.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <div className="text-3xl mb-2">🔔</div>
                <p className="text-sm">{t('कोई सूचना नहीं', 'No notifications')}</p>
              </div>
            ) : (
              notifs.map(n => {
                const style = TYPE_STYLES[n.type] || TYPE_STYLES.system;
                return (
                  <div
                    key={n.notification_id}
                    onClick={() => !n.read && markRead(n.notification_id)}
                    className={`px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-gray-50 transition ${!n.read ? 'bg-orange-50/50' : ''}`}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className="flex gap-2.5">
                      <div className={`w-8 h-8 rounded-lg ${style.bg} ${style.border} border flex items-center justify-center text-sm flex-shrink-0`}>
                        {style.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-semibold ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</span>
                          <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1.5" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
